import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ReactNode, forwardRef } from 'react'
import { cn } from '~/utils/cn'

export const DropdownMenuRoot = ({
  children,
  ...props
}: DropdownMenu.DropdownMenuProps) => (
  <div>
    <style jsx global>
      {`
        .talo-dropdown-content::-webkit-scrollbar {
          width: 0.25rem;
        }

        .talo-dropdown-content::-webkit-scrollbar-track {
          background: #e5e7eb;
          border-radius: 9999px;
        }

        .talo-dropdown-content::-webkit-scrollbar-thumb {
          background: #9ca3af;
          border-radius: 9999px;
        }
      `}
    </style>
    <DropdownMenu.Root {...props}>{children}</DropdownMenu.Root>
  </div>
)

export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  DropdownMenu.DropdownMenuContentProps & {
    footerContent?: ReactNode
    showSeparatorAboveFooter?: boolean
  }
>(
  (
    { className, children, footerContent, showSeparatorAboveFooter, ...props },
    forwardedRef
  ) => (
    <DropdownMenu.Content
      className={cn(
        'z-[999] min-w-[12rem] rounded-md shadow-lg border border-gray-200 bg-white p-2 will-change-[opacity,transform] data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade',
        className
      )}
      {...props}
      ref={forwardedRef}
    >
      <div className="talo-dropdown-content max-h-80 overflow-y-auto">
        {children}
      </div>
      {showSeparatorAboveFooter ? (
        <DropdownMenuSeparator className="m-1 h-px bg-gray-300" />
      ) : null}
      {footerContent}
    </DropdownMenu.Content>
  )
)

export const DropdownMenuSubTrigger = forwardRef<
  HTMLDivElement,
  DropdownMenu.DropdownMenuSubTriggerProps
>(({ className, children, ...props }, forwardedRef) => (
  <DropdownMenu.SubTrigger
    className={cn(
      (className =
        'relative flex gap-2 p-2 select-none items-center rounded-md leading-none text-sm outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-gray-100 data-[highlighted]:data-[state=open]:bg-gray-100 data-[state=open]:bg-gray-100 data-[disabled]:text-gray-400'),
      className
    )}
    {...props}
    ref={forwardedRef}
  >
    {children}
  </DropdownMenu.SubTrigger>
))

export const DropdownMenuSubContent = forwardRef<
  HTMLDivElement,
  DropdownMenu.DropdownMenuSubContentProps & {
    footerContent?: ReactNode
    showSeparatorAboveFooter?: boolean
  }
>(
  (
    { className, children, footerContent, showSeparatorAboveFooter, ...props },
    forwardedRef
  ) => (
    <DropdownMenu.SubContent
      className={cn(
        (className =
          'z-[999] min-w-[12rem] rounded-md bg-white p-2 shadow-lg border border-gray-200 will-change-[opacity,transform] data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade'),
        className
      )}
      {...props}
      ref={forwardedRef}
    >
      <div className="talo-dropdown-content max-h-80 overflow-y-auto">
        {children}
      </div>
      {showSeparatorAboveFooter ? (
        <DropdownMenuSeparator className="m-1 h-px bg-gray-300" />
      ) : null}
      {footerContent}
    </DropdownMenu.SubContent>
  )
)

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  DropdownMenu.DropdownMenuItemProps
>(({ className, children, ...props }, forwardedRef) => (
  <DropdownMenu.Item
    className={cn(
      (className =
        'relative flex gap-2 p-2 py-2.5 select-none items-center rounded-md text-sm leading-none outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-gray-100 data-[disabled]:text-gray-400'),
      className
    )}
    {...props}
    ref={forwardedRef}
  >
    {children}
  </DropdownMenu.Item>
))

export const DropdownMenuTrigger = DropdownMenu.Trigger
export const DropdownMenuPortal = DropdownMenu.Portal
export const DropdownMenuSeparator = DropdownMenu.Separator
export const DropdownMenuSub = DropdownMenu.Sub
